import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Bot, User, Loader2, RefreshCw, MessageSquare, Zap, AlertCircle, Copy, Check, Mic, MicOff } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import VoiceRecorder from './VoiceRecorder';

const TalkToTickSync = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [dataSummary, setDataSummary] = useState(null);
  const [isProcessingData, setIsProcessingData] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const messagesEndRef = useRef(null);
  const fullResponseRef = useRef('');
  const [currentQuestionSet, setCurrentQuestionSet] = useState(0);
  const [currentDescription, setCurrentDescription] = useState(0);
  const [isNewChatStarted, setIsNewChatStarted] = useState(false);
  const [lastClickTime, setLastClickTime] = useState(0);
  const [serviceStatus, setServiceStatus] = useState({ status: 'unknown', services: {} });
  const [retryCount, setRetryCount] = useState(0);
  const [copiedMessages, setCopiedMessages] = useState(new Set());
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  
  // Add refs for cleanup
  const abortControllerRef = useRef(null);
  const timeoutRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const isMountedRef = useRef(true);
  
  // Constants
  const STREAMING_TIMEOUT = 30000; // 30 seconds
  const POLL_INTERVAL = 5000; // 5 seconds
  const MAX_POLL_DURATION = 300000; // 5 minutes
  const DEBOUNCE_DELAY = 300; // 300ms - reduced for better responsiveness

  // Scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    if (isMountedRef.current) {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Reset streaming state safely
  const resetStreamingState = useCallback(() => {
    if (isMountedRef.current) {
      setIsStreaming(false);
      setIsLoading(false);
    }
  }, []);

  // Get user-friendly error message
  const getErrorMessage = useCallback((error) => {
    if (error.name === 'AbortError') {
      return 'Request was cancelled. Please try again.';
    }
    if (error.message.includes('Failed to send message')) {
      if (serviceStatus.status === 'unhealthy') {
        return 'AI services are temporarily unavailable. Please try again in a few minutes.';
      } else if (serviceStatus.services?.ai?.circuit_breaker_state === 'open') {
        return 'AI service is temporarily overloaded. Please try again in a few minutes.';
      } else {
        return 'Unable to connect to AI service. Please check your connection and try again.';
      }
    }
    return 'Sorry, I encountered an error. Please try again.';
  }, [serviceStatus]);

  // Sanitize user input
  const sanitizeInput = useCallback((input) => {
    return input.trim().replace(/[<>]/g, ''); // Basic XSS protection
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [cleanup]);

  // Define functions first before using them in useEffect
  const checkServiceHealth = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setServiceStatus({ status: 'unhealthy', services: {} });
        return;
      }
      
      const response = await axios.get('/api/chat/health', {
        timeout: 5000 // 5 second timeout
      });
      
      const health = response.data;
      
      // Update state with functional update
      setServiceStatus(prevStatus => {
        const newStatus = {
          status: health.status,
          services: health.services,
          timestamp: health.timestamp
        };
        
        // Only update if status actually changed
        if (prevStatus.status !== newStatus.status) {
          return newStatus;
        }
        return prevStatus;
      });
    } catch (error) {
      if (error.name !== 'AbortError' && isMountedRef.current) {
        setServiceStatus(prevStatus => {
          if (prevStatus.status !== 'unhealthy') {
            return { status: 'unhealthy', services: {} };
          }
          return prevStatus;
        });
      }
    }
  }, []); // Empty dependency array is correct here

  const loadDataSummary = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    try {
      const response = await axios.get('/api/chat/data-summary', {
        timeout: 10000 // 10 second timeout
      });
      
      const data = response.data;
      if (isMountedRef.current) {
        setDataSummary(data);
        // Set last refresh time from backend
        if (data.last_refresh_time) {
          setLastRefreshTime(data.last_refresh_time);
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
        // Error handled silently
      }
    }
  }, []);

  const pollRefreshStatus = useCallback(async (taskId) => {
    if (!isMountedRef.current) return;
    
      const token = localStorage.getItem('token');
      
    // Clear any existing polling interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    pollIntervalRef.current = setInterval(async () => {
      if (!isMountedRef.current) {
        clearInterval(pollIntervalRef.current);
        return;
      }
      
      try {
        const response = await axios.get(`/api/chat/refresh-status/${taskId}`, {
          timeout: 10000 // 10 second timeout
        });
        
        const status = response.data;
        
        if (status.status === 'completed') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          if (isMountedRef.current) {
            setIsProcessingData(false);
            
            // Clear localStorage when task completes
            localStorage.removeItem('refresh_task_id');
            localStorage.removeItem('refresh_processing');
            setCurrentTaskId(null);
            
            if (status.success) {
              // Reload data summary to get updated timestamp
              await loadDataSummary();
            }
          }
        } else if (status.status === 'failed') {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          if (isMountedRef.current) {
            setIsProcessingData(false);
            
            // Clear localStorage when task fails
            localStorage.removeItem('refresh_task_id');
            localStorage.removeItem('refresh_processing');
            setCurrentTaskId(null);
          }
        } else if (status.status === 'SUCCESS') {
          // Handle Celery SUCCESS state
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          if (isMountedRef.current) {
            setIsProcessingData(false);
            
            // Clear localStorage when task completes
            localStorage.removeItem('refresh_task_id');
            localStorage.removeItem('refresh_processing');
            setCurrentTaskId(null);
            
            // Reload data summary to get updated timestamp
            await loadDataSummary();
          }
        } else if (status.status === 'FAILURE') {
          // Handle Celery FAILURE state
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          if (isMountedRef.current) {
            setIsProcessingData(false);
            
            // Clear localStorage when task fails
            localStorage.removeItem('refresh_task_id');
            localStorage.removeItem('refresh_processing');
            setCurrentTaskId(null);
          }
        }
        // Continue polling for pending/progress states
      } catch (error) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        if (isMountedRef.current) {
          setIsProcessingData(false);
          // Error handled silently
          // Clear localStorage on error
          localStorage.removeItem('refresh_task_id');
          localStorage.removeItem('refresh_processing');
          setCurrentTaskId(null);
        }
      }
    }, POLL_INTERVAL);
    
    // Clear interval after max duration to prevent infinite polling
    timeoutRef.current = setTimeout(() => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (isMountedRef.current && isProcessingData) {
        setIsProcessingData(false);
        // Clear localStorage on timeout
        localStorage.removeItem('refresh_task_id');
        localStorage.removeItem('refresh_processing');
        setCurrentTaskId(null);
      }
    }, MAX_POLL_DURATION);
  }, [isProcessingData, loadDataSummary]);

  // Check for ongoing refresh task from localStorage
  const checkOngoingRefreshTask = useCallback(async () => {
    const savedTaskId = localStorage.getItem('refresh_task_id');
    const savedProcessingState = localStorage.getItem('refresh_processing');
    
    if (savedTaskId && savedProcessingState === 'true') {
      setCurrentTaskId(savedTaskId);
      setIsProcessingData(true);
      
      // Resume polling for this task
      pollRefreshStatus(savedTaskId);
    }
  }, [pollRefreshStatus]);

  // Load data summary on component mount
  useEffect(() => {
    loadDataSummary();
    // Check for ongoing refresh task
    checkOngoingRefreshTask();
    // Check service health
    checkServiceHealth();
    
    // Set up periodic health checks every 30 seconds
    const healthCheckInterval = setInterval(() => {
      checkServiceHealth();
    }, 30000);
    
    return () => clearInterval(healthCheckInterval);
  }, [loadDataSummary, checkServiceHealth, checkOngoingRefreshTask]);

  // Rotate questions every 10 seconds
  useEffect(() => {
    const questionInterval = setInterval(() => {
      setCurrentQuestionSet(prev => (prev + 1) % questionSets.length);
    }, 10000);

    return () => clearInterval(questionInterval);
  }, []);

  // Rotate descriptions every 5 seconds
  useEffect(() => {
    const descriptionInterval = setInterval(() => {
      setCurrentDescription(prev => (prev + 1) % descriptions.length);
    }, 5000);

    return () => clearInterval(descriptionInterval);
  }, []);

  // Cleanup localStorage on component unmount
  useEffect(() => {
    return () => {
      // Only clear if we're not currently processing (to avoid clearing active tasks)
      if (!isProcessingData) {
        localStorage.removeItem('refresh_task_id');
        localStorage.removeItem('refresh_processing');
      }
    };
  }, [isProcessingData]);

  const processUserData = useCallback(async () => {
    
    if (!isMountedRef.current) return;
    
    // Prevent multiple simultaneous requests
    if (isProcessingData) {
      return;
    }
    
    setIsProcessingData(true);
    try {
      const response = await axios.post('/api/chat/refresh-data', {
        force_refresh: false
      });
      
      const result = response.data;
      
      if (result.success && isMountedRef.current) {
        // Save task ID and processing state to localStorage
        setCurrentTaskId(result.task_id);
        localStorage.setItem('refresh_task_id', result.task_id);
        localStorage.setItem('refresh_processing', 'true');
        
        // Check if task completed immediately (direct processing)
        if (result.status === 'completed') {
          setIsProcessingData(false);
          localStorage.removeItem('refresh_task_id');
          localStorage.removeItem('refresh_processing');
          setCurrentTaskId(null);
          
          // Reload data summary
          await loadDataSummary();
        } else {
          // Start polling for task completion
          pollRefreshStatus(result.task_id);
        }
      } else if (isMountedRef.current) {
        setIsProcessingData(false);
      }
    } catch (error) {
      if (isMountedRef.current) {
        setIsProcessingData(false);
        // Clear localStorage on error
        localStorage.removeItem('refresh_task_id');
        localStorage.removeItem('refresh_processing');
      }
    }
  }, [isProcessingData, loadDataSummary, pollRefreshStatus]);

  // Consolidated message sending function
  const sendMessage = useCallback(async (messageContent = null) => {
    const messageToSend = messageContent || inputMessage;
    
    
    // Prevent sending if already processing or empty message
    if (!messageToSend.trim() || isLoading || isStreaming || !isMountedRef.current) {
      return;
    }

    // Sanitize input
    const sanitizedMessage = sanitizeInput(messageToSend);
    if (!sanitizedMessage) {
      return;
    }

    // Clear any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Add user message first
    const userMessage = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role: 'user',
      content: sanitizedMessage,
      timestamp: new Date()
    };

    if (isMountedRef.current) {
    setMessages(prev => [...prev, userMessage]);
      if (!messageContent) {
        setInputMessage(''); // Clear input only if not using direct message
      }
    setIsLoading(true);
    setIsStreaming(true);
      setIsNewChatStarted(false);
      
    }

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();
    
    // Set timeout for streaming
    timeoutRef.current = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    }, STREAMING_TIMEOUT);

    try {
      const response = await axios.post('/api/chat/stream', {
        message: sanitizedMessage,
        conversation_id: conversationId
      }, {
        responseType: 'stream',
        signal: abortControllerRef.current.signal
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      fullResponseRef.current = '';
      let aiMessageAdded = false;

      while (true) {
        if (!isMountedRef.current) break;
        
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'chunk') {
                fullResponseRef.current += data.content;
                
                // Add AI message only on first chunk
                if (!aiMessageAdded && isMountedRef.current) {
                  setMessages(prev => [...prev, {
                    id: `${Date.now()}-ai-${Math.random().toString(36).substr(2, 9)}`,
                    role: 'assistant',
                    content: fullResponseRef.current,
                    isStreaming: true,
                    timestamp: new Date()
                  }]);
                  aiMessageAdded = true;
                } else if (isMountedRef.current) {
                  // Update existing AI message
                  setMessages(prev => {
                    const newMessages = [...prev];
                    if (newMessages.length > 0 && newMessages[newMessages.length - 1].role === 'assistant') {
                      newMessages[newMessages.length - 1] = {
                        ...newMessages[newMessages.length - 1],
                        content: fullResponseRef.current,
                        isStreaming: true
                      };
                    }
                    return newMessages;
                  });
                }
              } else if (data.type === 'complete') {
                // Finalize the message
                if (isMountedRef.current) {
                setMessages(prev => {
                  const newMessages = [...prev];
                  if (newMessages.length > 0 && newMessages[newMessages.length - 1].role === 'assistant') {
                    newMessages[newMessages.length - 1] = {
                      ...newMessages[newMessages.length - 1],
                      content: data.message,
                      isStreaming: false
                    };
                  }
                  return newMessages;
                });
                
                // Set streaming to false when complete
                setIsStreaming(false);
                
                if (data.conversation_id) {
                  setConversationId(data.conversation_id);
                  }
                }
              } else if (data.type === 'error') {
                throw new Error(data.message);
              }
            } catch (e) {
              console.error('Error parsing streaming data:', e);
            }
          }
        }
      }
    } catch (error) {
      if (isMountedRef.current) {
      console.error('Error sending message:', error);
      
        const errorMessage = getErrorMessage(error);
      
      setMessages(prev => {
        const newMessages = [...prev];
        if (newMessages.length > 0) {
          newMessages[newMessages.length - 1] = {
            ...newMessages[newMessages.length - 1],
            content: errorMessage,
            isError: true,
            isStreaming: false
          };
        }
        return newMessages;
      });
      
      // Increment retry count and check service health
      setRetryCount(prev => prev + 1);
      if (retryCount >= 2) {
        checkServiceHealth();
        setRetryCount(0);
        }
      }
    } finally {
      if (isMountedRef.current) {
      setIsLoading(false);
      // Don't set isStreaming to false here - let the complete handler do it
    }
      
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      // Clear abort controller
      abortControllerRef.current = null;
    }
  }, [inputMessage, isLoading, isStreaming, conversationId, sanitizeInput, getErrorMessage, checkServiceHealth, retryCount, cleanup]);

  const formatTime = useCallback((timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }, []);

  const startNewConversation = useCallback(() => {
    if (!isMountedRef.current) return;
    
    // Cleanup any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    
    setMessages([]);
    setConversationId(null);
    setIsLoading(false);
    setIsStreaming(false);
    setInputMessage('');
    setIsNewChatStarted(true);
  }, []);

  // Debounced click handler for example questions
  const handleExampleQuestionClick = useCallback((question) => {
    const now = Date.now();
    
    // Prevent rapid clicks but allow reasonable interaction
    if (now - lastClickTime < DEBOUNCE_DELAY) {
      return;
    }
    setLastClickTime(now);
    
    // Check if we can send a message
    if (isLoading || isStreaming || !isMountedRef.current) {
      return;
    }
    
    sendMessage(question);
  }, [lastClickTime, isLoading, isStreaming, sendMessage]);

  // Copy message function
  const copyMessage = useCallback(async (messageId, content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessages(prev => new Set([...prev, messageId]));
      
      // Remove the copied state after 2 seconds
      setTimeout(() => {
        setCopiedMessages(prev => {
          const newSet = new Set(prev);
          newSet.delete(messageId);
          return newSet;
        });
      }, 2000);
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  }, []);

  // Voice recording handlers
  const handleVoiceMessageSend = async (audioBlob) => {
    try {
      const formData = new FormData();
      formData.append('voice_note', audioBlob, 'voice_note.webm');
      
      const response = await axios.post('/api/chat/voice-to-text', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const data = response.data;
      const transcribedText = data.text;
      
      if (transcribedText && transcribedText.trim()) {
        setInputMessage(transcribedText);
        sendMessage(transcribedText);
        setInputMessage(''); // Clear the input after sending
      }
    } catch (error) {
      console.error('Error sending voice message:', error);
      alert('Failed to process voice message. Please try again.');
    } finally {
      setShowVoiceRecorder(false);
    }
  };

  const handleVoiceRecorderCancel = () => {
    setShowVoiceRecorder(false);
  };

  const toggleVoiceRecorder = () => {
    setShowVoiceRecorder(true);
  };

  // Role-specific question sets
  const adminQuestionSets = [
    [
      "Show me all users and their activity",
      "What are the system-wide performance metrics?",
      "Generate a comprehensive team productivity report"
    ],
    [
      "Which projects are behind schedule?",
      "Show me all pending leave requests",
      "What are the top performing team members?"
    ],
    [
      "Analyze system usage patterns",
      "Show me all invoices and their status",
      "What are the recent security activities?"
    ],
    [
      "Generate executive summary for this month",
      "Show me all documentation updates",
      "What are the system health metrics?"
    ]
  ];

  const memberQuestionSets = [
    [
      "How much time did I spend on projects this week?",
      "What are my current tasks and their status?",
      "Show me my most productive hours"
    ],
    [
      "What projects am I working on?",
      "Generate a summary of my work this month",
      "What are my upcoming deadlines?"
    ],
    [
      "Show me my time tracking patterns",
      "Which tasks took the longest this week?",
      "How can I improve my productivity?"
    ],
    [
      "What's my average work hours per day?",
      "Show me my project progress",
      "What tasks are overdue?"
    ]
  ];

  const projectLeadQuestionSets = [
    [
      "Show me my team's current tasks",
      "What projects are my team working on?",
      "Generate a team productivity report"
    ],
    [
      "Which team members are behind schedule?",
      "Show me project progress for my team",
      "What are the upcoming team deadlines?"
    ],
    [
      "Analyze my team's time tracking patterns",
      "Show me team performance metrics",
      "How can I improve team productivity?"
    ],
    [
      "What's my team's average work hours?",
      "Show me team project progress",
      "Which team tasks are overdue?"
    ]
  ];

  // Select question sets based on user role
  const questionSets = user?.role === 'admin' ? adminQuestionSets : 
                      user?.role === 'project_lead' ? projectLeadQuestionSets : 
                      memberQuestionSets;

  // Role-specific descriptions
  const adminDescriptions = [
    "I can help you analyze system-wide data, monitor team performance, and provide comprehensive organizational insights.",
    "Ask me about user activities, system metrics, and organizational analytics to get executive-level reports.",
    "I can provide detailed reports on team productivity, project status, and system-wide performance metrics.",
    "Get instant answers about organizational health, team performance, and system analytics with AI-powered analysis."
  ];

  const memberDescriptions = [
    "I can help you analyze your work data, track time patterns, and answer questions about your projects and tasks.",
    "Ask me about your productivity insights, project timelines, and work patterns to get valuable analytics.",
    "I can provide detailed reports on your time allocation, task completion rates, and performance metrics.",
    "Get instant answers about your work progress, deadlines, and productivity trends with AI-powered analysis."
  ];

  const projectLeadDescriptions = [
    "I can help you analyze your team's performance, track project progress, and provide team management insights.",
    "Ask me about team productivity, project timelines, and team performance patterns to get valuable analytics.",
    "I can provide detailed reports on team time allocation, task completion rates, and team performance metrics.",
    "Get instant answers about team progress, deadlines, and team productivity trends with AI-powered analysis."
  ];

  // Select descriptions based on user role
  const descriptions = user?.role === 'admin' ? adminDescriptions : 
                      user?.role === 'project_lead' ? projectLeadDescriptions : 
                      memberDescriptions;

  const getExampleQuestions = () => questionSets[currentQuestionSet];

  return (
    <div className="flex flex-col h-full bg-gray-50 relative">
      {/* Floating Controls - Only show when no messages */}
      {messages.length === 0 && (
        <div className="absolute top-4 right-4 z-10">
          <div className="flex items-center space-x-3">
            {/* Service Status Indicator */}
            <div className="flex items-center space-x-2">
              <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${
                serviceStatus.status === 'healthy' ? 'bg-emerald-400' : 
                serviceStatus.status === 'unhealthy' ? 'bg-red-400' : 'bg-amber-400'
              }`}></div>
            </div>
            
            {lastRefreshTime && (
              <div className="text-xs text-gray-400">
                Last synced: {lastRefreshTime}
              </div>
            )}
            <button
              onClick={() => {
                processUserData();
              }}
              disabled={isProcessingData}
              className={`flex items-center space-x-1.5 px-3 py-1.5 text-xs rounded-md transition-all duration-200 ${
                isProcessingData 
                  ? 'bg-blue-200 text-blue-600 cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700 border border-blue-600 hover:border-blue-700'
              }`}
            >
              {isProcessingData ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>Syncing</span>
                </>
              ) : (
                <>
                  <RefreshCw className="h-3 w-3" />
                  <span>Sync</span>
                </>
              )}
            </button>
            
          </div>
        </div>
      )}


      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-16">
            {/* AI Icon with gradient background */}
            <div className="mb-8">
              <div className="p-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full w-24 h-24 mx-auto flex items-center justify-center shadow-lg">
                <Bot className="h-12 w-12 text-white" />
              </div>
            </div>
            
            {/* Welcome Message */}
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Welcome to Pulse AI!</h3>
            <p className="text-gray-600 mb-8 max-w-lg mx-auto text-lg leading-relaxed transition-all duration-500 ease-in-out">
              {descriptions[currentDescription]}
            </p>
            
         
            
            {/* Data Processing Alert */}
            {dataSummary && dataSummary.user_data?.document_count === 0 && (
              <div className="mb-8 p-6 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl max-w-lg mx-auto shadow-sm">
                <div className="flex items-center mb-3">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></div>
                  <p className="text-sm font-medium text-yellow-800">
                    No data indexed yet
                  </p>
                </div>
                <p className="text-sm text-yellow-700 mb-4">
                  Click "Sync" to process your work information and start asking questions.
                </p>
                <button
                  onClick={() => {
                    processUserData();
                  }}
                  disabled={isProcessingData}
                  className="w-full px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg hover:from-yellow-600 hover:to-orange-600 disabled:opacity-50 transition-all duration-200 font-medium shadow-md hover:shadow-lg"
                >
                  {isProcessingData ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Processing...
                    </div>
                  ) : (
                    'Process My Data'
                  )}
                </button>
              </div>
            )}
            
            {/* Example Questions - Only show when data is indexed */}
            {dataSummary && dataSummary.user_data?.document_count > 0 && (
              <div className="max-w-4xl mx-auto">
                <div className="flex items-center justify-center mb-6">
                  <Zap className="h-5 w-5 text-blue-500 mr-2" />
                  <p className="text-lg font-semibold text-gray-700">Try asking</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 transition-all duration-500 ease-in-out">
                  {getExampleQuestions().map((question, index) => (
                    <button
                      key={`${currentQuestionSet}-${index}`}
                      onClick={() => {
                        handleExampleQuestionClick(question);
                      }}
                      className={`group p-4 bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-lg transition-all duration-200 text-left transform hover:scale-105 fade-in-up ${(isLoading || isStreaming) ? 'opacity-50 cursor-not-allowed' : ''}`}
                      style={{
                        animationDelay: `${index * 0.1}s`
                      }}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center group-hover:from-blue-200 group-hover:to-purple-200 transition-colors">
                          <MessageSquare className="h-4 w-4 text-blue-600" />
                        </div>
                        <p className="text-sm text-gray-700 group-hover:text-gray-900 font-medium leading-relaxed">
                          {question}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-3xl px-4 py-3 rounded-lg ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : message.isError
                    ? 'bg-red-50 text-red-800 border border-red-200'
                    : message.isSystemMessage
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-white text-gray-900 border border-gray-200'
                }`}
              >
                <div className="flex items-start space-x-2">
                  {message.role === 'assistant' && !message.isError && (
                    <Bot className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  )}
                  {message.role === 'assistant' && message.isError && (
                    <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0 text-red-500" />
                  )}
                  {message.role === 'user' && (
                    <User className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  )}
                                     <div className="flex-1">
                     <div className={`leading-relaxed ${message.role === 'user' ? 'text-white' : 'text-gray-700'}`}>
                       {message.role === 'assistant' ? (
                        <div className="prose prose-sm max-w-none prose-headings:font-semibold prose-headings:text-gray-900 prose-h2:text-lg prose-h3:text-base prose-h2:mt-4 prose-h3:mt-3 prose-h2:mb-2 prose-h3:mb-2 prose-p:mb-3 prose-p:leading-relaxed prose-ul:my-2 prose-ul:space-y-1 prose-ol:my-2 prose-ol:space-y-1 prose-li:my-0.5 prose-strong:text-gray-900 prose-strong:font-semibold prose-code:bg-gray-100 prose-code:text-gray-800 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:font-mono prose-code:before:content-[''] prose-code:after:content-[''] prose-table:my-3 prose-table:w-full prose-table:border-collapse prose-th:border prose-th:border-gray-300 prose-th:bg-gray-50 prose-th:px-2 prose-th:py-1.5 prose-th:text-left prose-th:text-xs prose-th:font-semibold prose-td:border prose-td:border-gray-200 prose-td:px-2 prose-td:py-1.5 prose-td:text-sm prose-blockquote:border-l-4 prose-blockquote:border-blue-300 prose-blockquote:bg-blue-50 prose-blockquote:py-1 prose-blockquote:px-3 prose-blockquote:italic prose-blockquote:my-2 prose-hr:my-4">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {message.content}
                          </ReactMarkdown>
                        </div>
                       ) : message.content}
                       {message.isStreaming && (
                         <div className="inline-flex items-center ml-1">
                           <div className="flex space-x-0.5">
                             <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                             <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                             <div className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                           </div>
                         </div>
                       )}
                     </div>
                     <div className="flex items-center justify-between mt-2">
                       <div className="text-xs opacity-70">
                         {formatTime(message.timestamp)}
                       </div>
                       <div className="flex items-center space-x-2">
                       {message.tokensUsed && (
                         <div className="text-xs opacity-70">
                           {message.tokensUsed} tokens
                         </div>
                       )}
                         <button
                           onClick={() => copyMessage(message.id, message.content)}
                           className={`p-1 rounded transition-colors ${
                             message.role === 'user' 
                               ? 'hover:bg-blue-700' 
                               : 'hover:bg-gray-100'
                           }`}
                           title="Copy message"
                         >
                           {copiedMessages.has(message.id) ? (
                             <Check className="h-3 w-3 text-green-400" />
                           ) : (
                             <Copy className={`h-3 w-3 ${
                               message.role === 'user' 
                                 ? 'text-blue-200 hover:text-white' 
                                 : 'text-gray-400 hover:text-gray-600'
                             }`} />
                           )}
                         </button>
                       </div>
                     </div>
                     {message.contextUsed && (
                       <div className="mt-2 text-xs opacity-70">
                         <div>Sources: {message.contextUsed.user_data_types?.join(', ') || 'App documentation'}</div>
                       </div>
                     )}
                   </div>
                </div>
              </div>
            </div>
          ))
        )}
        
        {/* AI is thinking indicator */}
                 {isStreaming && messages.length > 0 && (
           <div className="flex justify-start">
             <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 shadow-sm">
              <div className="flex items-center space-x-3">
               <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                    <Bot className="h-3.5 w-3.5 text-white" />
                  </div>
                 <div className="flex items-center space-x-1">
                    <span className="text-sm text-gray-700 font-medium">AI is thinking</span>
                    <div className="flex space-x-0.5">
                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-pulse" style={{ animationDelay: '300ms' }}></div>
                    </div>
                   </div>
                 </div>
               </div>
             </div>
           </div>
         )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-6 py-4">
        {/* New Chat Button - Only show when messages exist */}
        {messages.length > 0 && (
          <div className="mb-3 flex justify-center">
            <button
              onClick={startNewConversation}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <MessageSquare className="h-4 w-4" />
              <span>New Chat</span>
            </button>
          </div>
        )}
        
        <div className="flex space-x-3">
          <div className="flex-1">
            {!showVoiceRecorder ? (
            <textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder={dataSummary && dataSummary.user_data?.document_count === 0 ? "Sync your data first to start asking questions..." : "Ask me anything about your work data..."}
              className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none bg-transparent ${
                dataSummary && dataSummary.user_data?.document_count === 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              rows="1"
              disabled={isLoading || (dataSummary && dataSummary.user_data?.document_count === 0)}
            />
            ) : (
              <VoiceRecorder
                onSend={handleVoiceMessageSend}
                onCancel={handleVoiceRecorderCancel}
                disabled={isLoading || isStreaming || (dataSummary && dataSummary.user_data?.document_count === 0)}
                autoStart={true}
              />
            )}
          </div>
          
          {/* Voice recording button */}
          {!showVoiceRecorder && (
            <button
              onClick={toggleVoiceRecorder}
              className={`flex items-center p-3 rounded-lg transition-all duration-200 ${
                'hover:bg-gray-100 text-gray-600 hover:text-blue-600'
              } ${isLoading || isStreaming || (dataSummary && dataSummary.user_data?.document_count === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={isLoading || isStreaming || (dataSummary && dataSummary.user_data?.document_count === 0)}
              title={dataSummary && dataSummary.user_data?.document_count === 0 ? "Sync your data first to use voice recording" : "Record Voice Message"}
            >
              <Mic className="h-5 w-5" />
            </button>
          )}
          
          <button
             onClick={() => {
               sendMessage();
             }}
             disabled={!inputMessage.trim() || isLoading || isStreaming || (dataSummary && dataSummary.user_data?.document_count === 0)}
             className={`px-4 py-3 rounded-lg flex items-center justify-center transition-all duration-200 ${
               !inputMessage.trim() || isLoading || isStreaming || (dataSummary && dataSummary.user_data?.document_count === 0)
                 ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                 : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg transform hover:scale-105'
             }`}
           >
             {isLoading ? (
               <div className="flex items-center space-x-2">
                 <Loader2 className="h-5 w-5 animate-spin" />
                 <span className="text-sm">Sending...</span>
               </div>
             ) : (
               <Send className="h-5 w-5" />
             )}
           </button>
        </div>
        
        
      </div>
    </div>
  );
};

export default TalkToTickSync;
